import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, LayoutDashboard, Leaf } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/mock";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";

export function Nav() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { if (searchOpen && !catalog.length) api.get("/content").then((r) => setCatalog(r.data)).catch(() => {}); }, [searchOpen, catalog.length]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return catalog.filter((c) => c.title.toLowerCase().includes(s) || (c.desc || "").toLowerCase().includes(s)).slice(0, 8);
  }, [q, catalog]);

  const catRoute = { course: "/courses", therapy: "/therapies", herb: "/herbs", digital: "/digital", membership: "/" };

  return (
    <>
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <Link to="/" className="logo"><img src="/ely-logo.webp" alt="Ely Wellbeing" style={{ height: 34, width: 34, borderRadius: "50%" }} /> Ely<b>Wellbeing</b></Link>
        <button className="icon-round nav-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">{open ? <X size={18} /> : <Menu size={18} />}</button>
        <div className={`nav-links${open ? " open" : ""}`} onClick={() => setOpen(false)}>
          {NAV_LINKS.map((l) => <Link key={l.to} to={l.to}>{l.label}</Link>)}
          {user && <Link to="/hub">My Hub</Link>}
        </div>
        <div className="nav-right">
          <button className="icon-round" onClick={() => setSearchOpen(true)} aria-label="Search"><Search size={18} /></button>
          {user ? (
            user.picture
              ? <img className="avatar-img" src={user.picture} alt="" onClick={() => navigate("/hub")} />
              : <span className="avatar" onClick={() => navigate("/hub")}>{(user.name || "E").charAt(0).toUpperCase()}</span>
          ) : (
            <button className="btn btn-primary" onClick={() => openAuth("login")}>Sign in</button>
          )}
        </div>
      </nav>

      {searchOpen && (
        <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}>
          <div className="panel wide">
            <button className="close" onClick={() => setSearchOpen(false)}><X size={18} /></button>
            <span className="eyebrow">SEARCH ELYWELLBEING</span>
            <div className="field" style={{ marginTop: ".6rem" }}>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reiki, NLP, sleep, Maat, herbs…" />
            </div>
            <div style={{ marginTop: "1rem", display: "grid", gap: ".4rem" }}>
              {results.length ? results.map((r) => (
                <button key={r.id} className="hrow" style={{ textAlign: "left", background: "none", border: 0, cursor: "pointer", borderTop: "1px solid var(--line)" }}
                  onClick={() => { setSearchOpen(false); r.category === "course" ? navigate(`/course/${r.id}`) : navigate(catRoute[r.category] || "/courses"); }}>
                  <div><strong>{r.title}</strong><small>{r.category} · {r.trust_label}</small></div>
                  <span className="pill">{r.price_gbp ? `£${r.price_gbp}` : "Partner"}</span>
                </button>
              )) : <p className="empty">{q ? "No results — try another phrase." : "Start typing to search courses, therapies, herbs and more."}</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Footer() {
  return (
    <footer>
      <div className="fwrap">
        <div>
          <div className="logo"><img src="/ely-logo.webp" alt="Ely Wellbeing" style={{ height: 38, width: 38, borderRadius: "50%", marginRight: 8, verticalAlign: "middle" }} /> Ely<b>Wellbeing</b></div>
          <p style={{ maxWidth: 360, opacity: .9 }}>{BRAND.tagline} A training centre & therapy hub blending modern practice with the ancient Wu'sabat path. {BRAND.location}.</p>
        </div>
        <div>
          <h4 style={{ color: "#fff" }}>Explore</h4>
          {NAV_LINKS.map((l) => <div key={l.to}><Link to={l.to}>{l.label}</Link></div>)}
        </div>
        <div>
          <h4 style={{ color: "#fff" }}>Your space</h4>
          <div><Link to="/hub">My Wellbeing Hub</Link></div>
          <div><Link to="/wusabat">The 11 Laws of Maat</Link></div>
        </div>
        <div>
          <h4 style={{ color: "#fff" }}>Legal</h4>
          <div><Link to="/terms" data-testid="footer-terms-link">Terms &amp; Conditions</Link></div>
          <div><Link to="/privacy" data-testid="footer-privacy-link">Privacy Policy</Link></div>
          <div><Link to="/refund-policy" data-testid="footer-refund-link">Refund &amp; Guarantee</Link></div>
        </div>
      </div>
      <div className="fbottom">© 2026 ElyWellbeing. “Know Thyself, Heal Thyself.” — As above, so below.</div>
    </footer>
  );
}
